import {DestroyRef, Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {MapToolsService} from '../services/map-tools.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {PointSymbologyLookup} from '../../common/models/point-symbology.model';
import {PointTypeLookup} from '../../common/models/point-type.model';
import {equals} from '../../common/service/data-util';
import {switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class PointLayerService {
    private readonly layerId = 'pointFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'point';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private readonly excludedPointTypes = [1, 2, 3, 4, 5, 100];

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    private readonly staticSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(
        [
            `@font-face {
                font-family:'NLRP Point Symbols';
                src: url('fonts/nlrp-point-symbols.otf');
            }`,
            `text.nlrp-point-icon {
                font-family:'NLRP Point Symbols';
                font-style:normal;
                text-decoration:inherit;
                -webkit-font-smoothing:antialiased;
                display:inline;
                width:auto;
                height:auto;
                line-height:normal;
                vertical-align:baseline;
                background-image:none;
                background-position:0 0;
                background-repeat:repeat;
                margin-top:0;
            }`,
            `text.nlrp-point-icon > tspan.nlrp-point-icon-background, text.nlrp-point-icon > tspan.nlrp-point-icon-foreground {
                dominant-baseline: central;
            }`
        ].join('\n')
    );

    private pointSymbologyLookups: PointSymbologyLookup[];

    private pointTypeLookups: PointTypeLookup[];

    private mouseDownInfo: {
        x: number;
        y: number;
        graphic: any;
        pristineGraphic: string;
        graphicX: number;
        graphicY: number;
        connectedLineGraphics: any[];
    };

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private spatialUtilService: SpatialUtilService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {
        if (lookupService.lookupsLoaded) {
            this.pointSymbologyLookups = this.lookupService
                .getLookups('pointSymbology')
                .filter((l: PointSymbologyLookup) => !this.excludedPointTypes.includes(l.pointType));
            this.pointTypeLookups = this.lookupService
                .getLookups('pointType')
                .filter((l: PointTypeLookup) => !this.excludedPointTypes.includes(l.id));
        }
        eventBus
            .on('nlr:lookups-loaded')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                this.pointSymbologyLookups = this.lookupService.getLookups('pointSymbology');
                this.pointTypeLookups = this.lookupService
                    .getLookups('pointType')
                    .filter((l: PointTypeLookup) => !this.excludedPointTypes.includes(l.id));
            });
    }

    getLayerSelector(): string {
        return '';
    }

    getStaticSvgStyle() {
        return this.staticSvgStyle;
    }

    getSvgStyle() {
        const pixelScaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (pixelScaleFactor === 0) {
            return '';
        }

        const pointScaleFactor = this.layerUtilService.getPointScaleFactor();

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const defaultBackgroundColor = showFeatures ? (mapBackgroundColor === 'white' ? 'white' : 'black') : 'transparent';

        const newSvgStyle = [
            ...this.pointSymbologyLookups
                .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor)
                .map(l => {
                    const fontSizePixels = pixelScaleFactor * l.foregroundFontSizePts;
                    const circleRadiusPoints = (pointScaleFactor * l.foregroundFontSizePts) / 2.0;
                    const foregroundColor =
                        l.customForegroundFontColourRGB && showFeatures ? `rgb(${l.customForegroundFontColourRGB})` : 'transparent';

                    return [
                        `circle[${this.featureTypeAttrName}="${l.pointType}"] {r: ${circleRadiusPoints};}`,

                        `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"] {font-size: ${fontSizePixels}px;}`,

                        `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-background {fill: ${defaultBackgroundColor};}`,

                        `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-foreground {fill: ${foregroundColor};}`,

                        `circle[${this.featureTypeAttrName}][data-selected] + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getSelectedColor()};}`,

                        `circle[${this.featureTypeAttrName}][data-hidden]:not([data-selected])` +
                            ` + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getHiddenColor()};}`,

                        `circle[${this.featureTypeAttrName}]:hover + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-foreground {animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,

                        `circle[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                            ` + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                            ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getHoverColor()};}`
                    ]
                        .map(s => `${this.layerSelector} ${s}`)
                        .join('\n');
                })
        ].join('\n');

        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        const pointFeatureLayer = this.spatialUtilService.getPointMapLayer();

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'graphic-node-add', (evt: any) => {
            const featureId = evt.graphic.attributes.id;
            this.removeNodes(featureId);
            this.addSymbolNode(evt);
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'graphic-node-remove', (evt: any) => {
            document
                .querySelectorAll(`${this.layerSelector} text[${this.featureIdAttrName}="${evt.graphic.attributes.id}"]`)
                .forEach(text => text.parentNode.removeChild(text));
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'graphics-clear', () => {
            console.info('graphics-clear');
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'edits-complete', () => {
            const {pointIds, pointTypes} = this.spatialUtilService.getPointGraphics().reduce(
                (acc: any, g: any) => {
                    acc.pointIds.push(g.attributes.id);
                    acc.pointTypes.push(g.attributes.type);
                    return acc;
                },
                {pointIds: [], pointTypes: []}
            );

            document.querySelectorAll(`${this.layerSelector} text[${this.featureIdAttrName}]`).forEach(text => {
                const pointId = parseInt(text.getAttribute(this.featureIdAttrName));
                const pointIdx = pointIds.indexOf(pointId);
                if (pointIdx === -1) {
                    text.parentNode.removeChild(text);
                } else {
                    const pointType = pointTypes[pointIdx];
                    const pointTypeAttrValue = parseInt(text.getAttribute(this.featureTypeAttrName));
                    if (pointTypeAttrValue !== pointType) {
                        const pointSymbologyLookup = this.getPointSymbologyLookup(pointType);
                        text.setAttribute(this.featureTypeAttrName, `${pointType}`);

                        const tspanBackground = text.childNodes[0];
                        const tspanForeground = text.childNodes[1];

                        tspanBackground.textContent = pointSymbologyLookup.backgroundFontKey || '';
                        tspanForeground.textContent = pointSymbologyLookup.foregroundFontKey;
                    }
                }
            });
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'graphic-draw', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

            const text: any = document.querySelector(`${this.layerSelector} text[${this.featureIdAttrName}="${featureId}"]`);
            if (text) {
                text.setAttribute('x', `${cx}`);
                text.setAttribute('y', `${cy}`);
                text.childNodes[1].setAttribute('x', `${cx}`);
            } else {
                this.addSymbolNode(evt);
            }
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'dbl-click', (evt: any) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
            if (this.isMoveEnabled(evt.graphic)) {
                evt.target.setAttribute('cursor', 'pointer');
            }
        });

        this.layerUtilService.addLayerEventHandler(pointFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
            if (this.isMoveEnabled(evt.graphic)) {
                evt.target.removeAttribute('cursor');
            }
        });

        this.layerUtilService.addLayerEventHandler(
            pointFeatureLayer,
            'mouse-down',
            (evt: {graphic: any; mapPoint: any; which: any; altKey: any; ctrlKey: any; shiftKey: any}) => {
                if (
                    evt.which !== 1 ||
                    evt.altKey ||
                    evt.ctrlKey ||
                    evt.shiftKey ||
                    !this.isMoveEnabled(evt.graphic) ||
                    !this.permissionsService.hasEditPermission(['EDIT_DESIGN'])
                ) {
                    return;
                }

                const {x, y} = evt.mapPoint;
                const graphic = evt.graphic;

                this.layerUtilService.suspendDragDrawTools(this.featureClass, graphic.attributes.id);

                this.mouseDownInfo = {
                    x: x,
                    y: y,
                    graphic: graphic,
                    pristineGraphic: evt.graphic.toJson(),
                    graphicX: graphic.geometry.x,
                    graphicY: graphic.geometry.y,
                    connectedLineGraphics: this.spatialUtilService
                        .getLineGraphics()
                        .filter((g: any) => [g.attributes.frompointid, g.attributes.topointid].includes(graphic.attributes.id))
                };
            }
        );

        const map = this.spatialUtilService.getMap();

        map.on('mouse-drag', (evt: any) => {
            if (!this.mouseDownInfo) {
                return;
            }

            const graphic = this.mouseDownInfo.graphic;

            graphic.geometry.x = graphic.attributes.x = evt.mapPoint.x + this.mouseDownInfo.graphicX - this.mouseDownInfo.x;
            graphic.geometry.y = graphic.attributes.y = evt.mapPoint.y + this.mouseDownInfo.graphicY - this.mouseDownInfo.y;

            graphic.draw();

            this.mouseDownInfo.connectedLineGraphics.forEach((cl: any) => {
                if (cl.attributes.frompointid === graphic.attributes.id) {
                    cl.geometry.paths[0][0][0] = graphic.geometry.x;
                    cl.geometry.paths[0][0][1] = graphic.geometry.y;
                } else {
                    cl.geometry.paths[0][1][0] = graphic.geometry.x;
                    cl.geometry.paths[0][1][1] = graphic.geometry.y;
                }
                cl.draw();
            });
        });

        map.on('mouse-out', () => this.onMouseDragEnd());

        map.on('mouse-up', () => this.onMouseDragEnd());

        return pointFeatureLayer;
    }

    private getPointSymbologyLookup(pointType: number) {
        const lookupViewMode = this.spatialUtilService.getMapBackground() === 'white' ? 'White' : 'Black';
        return this.pointSymbologyLookups.find(l => l.pointType === pointType && l.viewMode === lookupViewMode);
    }

    private addSymbolNode(evt: {graphic: any; node: any}) {
        const featureId = evt.graphic.attributes.id;
        const featureType = evt.graphic.attributes.type;

        document
            .querySelectorAll(`${this.layerSelector} [${this.featureIdAttrName}="${featureId}"]`)
            .forEach(node => node.parentNode.removeChild(node));

        const pointSymbologyLookup = this.getPointSymbologyLookup(featureType);

        evt.node.setAttribute(this.featureTypeAttrName, `${featureType}`);
        evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
        evt.node.setAttribute('style', 'fill-opacity:0;stroke-opacity:0');
        this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

        const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
        text.setAttribute('pointer-events', 'none');
        text.setAttribute(this.featureIdAttrName, `${featureId}`);
        text.setAttribute(this.featureTypeAttrName, `${featureType}`);
        text.setAttribute('class', 'nlrp-point-icon');
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('x', `${cx}`);
        text.setAttribute('y', `${cy}`);

        const tspanBackground = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspanBackground.setAttribute('class', 'nlrp-point-icon-background');
        tspanBackground.textContent = pointSymbologyLookup.backgroundFontKey || '';
        text.appendChild(tspanBackground);

        const tspanForeground = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspanForeground.setAttribute('class', 'nlrp-point-icon-foreground');
        tspanForeground.setAttribute('x', `${cx}`);
        tspanForeground.textContent = pointSymbologyLookup.foregroundFontKey;
        text.appendChild(tspanForeground);

        document.getElementById(this.layerId).insertBefore(text, evt.node.nextSibling);
    }

    private removeNodes(featureId: number) {
        const layerElt = document.getElementById(this.layerId);

        layerElt.querySelectorAll(`g[${this.featureIdAttrName}="${featureId}"]`).forEach(g => g.parentNode.removeChild(g));

        const circle = layerElt.querySelector(`circle[${this.featureIdAttrName}="${featureId}"]`);
        if (circle) {
            circle.parentNode.removeChild(circle);
        }

        const text = document.querySelector(`${this.layerSelector} text[${this.featureIdAttrName}="${featureId}"]`);
        if (text) {
            text.parentNode.removeChild(text);
        }
    }

    private isMoveEnabled(graphic: any) {
        return (
            this.mapToolsService.isScaleActive() &&
            this.pointTypeLookups.find(ptl => ptl.id === graphic.attributes.type).moveEnabled &&
            (this.mapToolsService.isMoveActive() || this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id))
        );
    }

    private onMouseDragEnd() {
        if (this.mouseDownInfo) {
            if (!equals(this.mouseDownInfo.pristineGraphic, this.mouseDownInfo.graphic.toJson())) {
                this.spatialUtilService.applyEdits(this.featureClass, null, [this.mouseDownInfo.graphic], null);

                const connectedLineGraphics = this.mouseDownInfo.connectedLineGraphics;
                if (connectedLineGraphics.length) {
                    // Delete then add line to ensure line is redrawn on subsequent point drags
                    this.spatialUtilService
                        .applyEdits('line', null, null, connectedLineGraphics)
                        .pipe(switchMap(() => this.spatialUtilService.applyEdits('line', connectedLineGraphics, null, null)))
                        .subscribe();
                }
            }

            this.layerUtilService.restoreDragDrawTools();
            this.mouseDownInfo = null;
        }
    }
}
